/**
 * -----------------------------------------------------------------------------
 * Document Listing Page
 * -----------------------------------------------------------------------------
 * This page is used to list all the documents of the organization.
 */

import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useDebouncedCallback } from "use-debounce";
import { useOrganisationDocumentsQuery } from "../../../api/network/adminApiServices";
import {
  setListingQueryParams,
  setModalsData,
  TListingQueryParams,
  TModalsState,
} from "../../../api/store/commonSlice";
import { TDocument } from "../../../api/types/Document";
import { TSelectboxOption } from "../../../components/admin/formFields";
import HeaderView from "../../../components/admin/headerView";
import AdminTable from "../../../components/adminTable";
import ListingPageSubHeader from "../../../components/listingPageSubHeader";
import ListingTableHeader from "../../../components/listingTableHeader";
import Pagination, {
  TPaginationSelected,
} from "../../../components/pagination";
import { TAdminTableRowData } from "../../../components/types";
import { APP_CONFIG } from "../../../constants/constants";
import { ACL_NAMES, useACL } from "../../../hooks/acl";
import { useAccountTimezone } from "../../../hooks/timezone";
import { routeUrls } from "../../../navigation/routeUrls";
import { SortingStatusWithReset } from "../../../utils/adminListings";
import { formattedDateTimeBasedOnTimezone } from "../../../utils/dateTime";
import { ROLE_ACCESS_LEVELS } from "../../admin-details/role/constants";
import { DOCUMENT_FILE_TYPES } from "./constants";
import AdminsDocumentCreateNew from "./createNewDocument";

const all_columns = ["Name", "File Name", "File Type", "Active", "Upload Date"];

const columnOrderingFields: Record<string, string> = {
  Name: "name",
  "File Name": "file_name",
  "File Type": "file_type",
  Active: "is_active",
  "Upload Date": "created_at",
};

const ScreenDashboardAdminDocuments = () => {
  const { t: tMain } = useTranslation();
  const { t: tFilters } = useTranslation("translation", {
    keyPrefix: "admins.filters",
  });
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.documents",
  });
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const { documents: orgDocumentsQueryParams } = listingQueryParams;
  const dispatch = useDispatch();
  const listingWrapperRef = React.useRef<HTMLDivElement>(null);

  const { tzForDateFns } = useAccountTimezone();

  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        documents: { ...orgDocumentsQueryParams, page: 1, search: value },
      })
    );
  }, 500);

  const handleOrderingChange = (ordering?: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        documents: {
          ...orgDocumentsQueryParams,
          ordering,
          page: 1,
        },
      })
    );
  };

  const { getUserACLAccessLevel } = useACL();
  // fetching document list data
  const {
    data: dataOrgDocuments,
    isFetching: isFetchingOrgDrivers,
    error,
  } = useOrganisationDocumentsQuery({
    ...orgDocumentsQueryParams,
    // 'search_fields' intentionally not added here as default search fields are defined in the API service
  });
  const { count, next, previous, results } = dataOrgDocuments || {};

  // smooth scroll listing to the top on page change
  useEffect(() => {
    listingWrapperRef?.current?.scrollTo({ behavior: "smooth", top: 0 });
  }, [dataOrgDocuments]);

  // preparing table data
  const tableData: TAdminTableRowData[] = !!results
    ? (results || ([] as TDocument[])).map(
        (item: TDocument, index: number) => ({
          navLink: `${routeUrls.dashboardChildren.adminChildren.documents}/${item.id}`,
          cellData: [
            item?.name, // "Document name",
            item?.file_name, // "File name",
            DOCUMENT_FILE_TYPES?.[
              item?.file_type?.toString() as keyof typeof DOCUMENT_FILE_TYPES
            ] ?? "-", // "File type",
            item?.is_active ? (
              <span className="text-field-success">{tMain("yes")}</span>
            ) : (
              <span className="text-field-error-dark">{tMain("no")}</span>
            ), // active:
            !!item?.created_at && !!tzForDateFns
              ? formattedDateTimeBasedOnTimezone(item?.created_at, tzForDateFns)
              : "-", // "Upload date",
          ],
          isActive: item?.is_active,
        })
      )
    : [];

  // rendering
  return (
    <>
      <HeaderView title={t("heading")} />
      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} pt-4`}>
        <ListingPageSubHeader
          heading={t("sub_heading")}
          {...(getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_DOCUMENT) >=
            ROLE_ACCESS_LEVELS.ALL && {
            buttonText: t("add_new"),
            buttonCallback: () => {
              dispatch(
                setModalsData({ ...modalsState, showUploadDocumentModal: true })
              );
            },
          })}
        />
        <ListingTableHeader
          heading={
            <div className="flex items-center gap-4">
              {/* Heading */}
              <span>{t("listing_heading")}</span>

              {/* Sorted by label */}
              {!!orgDocumentsQueryParams?.ordering && (
                <SortingStatusWithReset
                  ordering={orgDocumentsQueryParams.ordering}
                  columnOrderingFields={columnOrderingFields}
                  onResetOrdering={() => handleOrderingChange(undefined)}
                />
              )}
            </div>
          }
          searchBoxPlaceholder={t("search_placeholder")}
          searchBoxValue={orgDocumentsQueryParams.search}
          searchBoxOnChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            debouncedSetSearchKeyword(e.target.value)
          }
          searchBoxOnClear={() => debouncedSetSearchKeyword("")}
        />
        <div className="py-4 mt-4 relative">
          <AdminTable
            viewportHeightOffset={"400"}
            columns={all_columns.map((item) => item)}
            data={tableData}
            isLoading={isFetchingOrgDrivers}
            listingQueryParams={orgDocumentsQueryParams}
            listingWrapperRef={listingWrapperRef}
            onOrderingChange={handleOrderingChange}
            ordering={orgDocumentsQueryParams.ordering}
            sortableColumns={columnOrderingFields}
          />
          {!isFetchingOrgDrivers && (
            <Pagination
              pageSize={orgDocumentsQueryParams.page_size}
              handlePageSizeChange={(e: TSelectboxOption | null) => {
                dispatch(
                  setListingQueryParams({
                    ...listingQueryParams,
                    documents: {
                      ...orgDocumentsQueryParams,
                      page: 1,
                      page_size: parseInt(`${e?.value}`),
                    },
                  })
                );
              }}
              totalPages={
                count ? Math.ceil(count / orgDocumentsQueryParams.page_size) : 1
              }
              forcePage={orgDocumentsQueryParams.page - 1}
              handlePageClick={(data: TPaginationSelected) => {
                dispatch(
                  setListingQueryParams({
                    ...listingQueryParams,
                    documents: {
                      ...orgDocumentsQueryParams,
                      page: data?.selected + 1,
                    },
                  })
                );
              }}
              paginCurrentPage={orgDocumentsQueryParams.page}
              paginItemsOnCurrentPage={results?.length || 0}
              paginTotalItemsCount={count}
            />
          )}
        </div>
      </div>
      <AdminsDocumentCreateNew />
    </>
  );
};

export default ScreenDashboardAdminDocuments;
